import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useExportGroupSheet } from '@/hooks/use-google-sheets';
import { useGroup } from '@/hooks/use-groups';
import { useImportGroupTsv } from '@/hooks/use-import-tsv';
import type { RegistrationListItem } from '@/hooks/use-registrations';
import { useGroupRegistrations } from '@/hooks/use-registrations';
import { api } from '@/lib/api';
import {
  getBalanceInvoiceOverviewStatus,
  getConsolidatedPaymentStatus,
  usesEffectiveSplitBilling,
  type BalanceInvoiceOverviewStatus,
  type ConsolidatedPaymentStatus,
} from '@/lib/registration-overview';
import { formatCurrency, formatDateTime, getEffectiveRegistrationField, getLocalizedText } from '@/lib/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { ArrowLeft, FileSpreadsheet, Pencil, PlaneLanding, PlaneTakeoff, Upload } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

type DepositInvoiceOverviewStatus = 'created' | 'partial' | 'failed' | 'not_created' | 'none';
const DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES = new Set(['paid', 'failed', 'expired', 'canceled']);

interface RegistrationOverviewRow {
  registration: RegistrationListItem;
  combinedCompanies: string;
  consolidatedPaymentStatus: ConsolidatedPaymentStatus;
  depositInvoiceStatus: DepositInvoiceOverviewStatus;
  balanceInvoiceStatus: BalanceInvoiceOverviewStatus;
  hasBalanceInvoice: boolean;
  canCreateBalanceInvoice: boolean;
  canCancelRegistration: boolean;
  canReactivateRegistration: boolean;
  canRegenerateMissingDepositInvoices: boolean;
}

function SelectionCheckbox({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={event => onChange(event.target.checked)}
      aria-label={ariaLabel}
      className="h-4 w-4 cursor-pointer rounded border border-primary accent-primary"
    />
  );
}

function getCombinedCompanyNames(registration: RegistrationListItem): string {
  const company1 = getEffectiveRegistrationField(
    registration.currentData,
    registration.manualOverrides,
    'invoice_company',
    ''
  ).value.trim();
  const company2 = getEffectiveRegistrationField(
    registration.currentData,
    registration.manualOverrides,
    'p2_invoice_company',
    ''
  ).value.trim();

  const uniqueCompanies = Array.from(
    new Set([company1, company2].filter(name => name && name !== '-' && name.toLowerCase() !== 'n/a'))
  );

  return uniqueCompanies.length ? uniqueCompanies.join(', ') : '-';
}

function hasBalanceInvoice(registration: RegistrationListItem): boolean {
  return registration.invoices.some(invoice => invoice.type === 'BALANCE');
}

function getDepositInvoiceOverviewStatus(registration: RegistrationListItem): DepositInvoiceOverviewStatus {
  const relevantPayments = registration.payments.filter(payment =>
    DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES.has(payment.status)
  );

  if (relevantPayments.length === 0) {
    return 'none';
  }

  const depositInvoices = registration.invoices.filter(invoice => invoice.type === 'DEPOSIT');
  const hasFailed = depositInvoices.some(invoice => invoice.exactStatus === 'FAILED');
  if (hasFailed) {
    return 'failed';
  }

  const createdCount = relevantPayments.filter(payment =>
    depositInvoices.some(
      invoice =>
        invoice.paymentId === payment.id &&
        (invoice.exactStatus === 'SYNCED' ||
          invoice.exactStatus === 'MATCHED' ||
          invoice.exactStatus === 'MANUALLY_RESOLVED')
    )
  ).length;

  if (createdCount === relevantPayments.length) {
    return 'created';
  }

  if (createdCount > 0) {
    return 'partial';
  }

  return 'not_created';
}

export function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedRegistrationIds, setSelectedRegistrationIds] = useState<Set<string>>(new Set());
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { data: group, isLoading: groupLoading } = useGroup(id!);
  const { data: registrations = [], isLoading: registrationsLoading } = useGroupRegistrations(id!);
  const exportSheet = useExportGroupSheet(id!);
  const {
    fileInputRef: tsvInputRef,
    handleFileSelected: handleTsvFileSelected,
    isPending: tsvImportPending,
  } = useImportGroupTsv(id!);
  const queryClient = useQueryClient();
  const groupInvoiceAdjustmentsStats = useMemo(() => {
    const adjustments = group?.invoiceAdjustments ?? [];
    const total = adjustments.reduce((sum, adjustment) => {
      const parsed = Number.parseFloat(String(adjustment.amount ?? '').replace(',', '.'));
      return Number.isFinite(parsed) ? sum + parsed : sum;
    }, 0);

    return {
      count: adjustments.length,
      total,
    };
  }, [group?.invoiceAdjustments]);
  const registrationRows = useMemo<RegistrationOverviewRow[]>(
    () =>
      [...registrations]
        .sort((a, b) => new Date(b.lastSubmittedAt).getTime() - new Date(a.lastSubmittedAt).getTime())
        .map(registration => {
          const combinedCompanies = getCombinedCompanyNames(registration);
          const consolidatedPaymentStatus = getConsolidatedPaymentStatus(registration);
          const depositInvoiceStatus = getDepositInvoiceOverviewStatus(registration);
          const balanceInvoiceStatus = getBalanceInvoiceOverviewStatus(registration);
          const balanceInvoicePresent = hasBalanceInvoice(registration);
          const canCreateBalance = !balanceInvoicePresent;
          const canCancel = !registration.cancelledAt && !balanceInvoicePresent;
          const canReactivate = Boolean(registration.cancelledAt) && !balanceInvoicePresent;
          const canRegenerateMissingDeposits = !balanceInvoicePresent
            ? registration.payments.some(
                payment =>
                  DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES.has(payment.status) &&
                  !registration.invoices.some(
                    invoice =>
                      invoice.type === 'DEPOSIT' &&
                      invoice.paymentId === payment.id &&
                      (invoice.exactStatus === 'SYNCED' ||
                        invoice.exactStatus === 'MATCHED' ||
                        invoice.exactStatus === 'MANUALLY_RESOLVED')
                  )
              )
            : false;

          return {
            registration,
            combinedCompanies,
            consolidatedPaymentStatus,
            depositInvoiceStatus,
            balanceInvoiceStatus,
            hasBalanceInvoice: balanceInvoicePresent,
            canCreateBalanceInvoice: canCreateBalance,
            canCancelRegistration: canCancel,
            canReactivateRegistration: canReactivate,
            canRegenerateMissingDepositInvoices: canRegenerateMissingDeposits,
          };
        }),
    [registrations]
  );

  const bulkAction = useMutation({
    mutationFn: async ({
      registrationIds,
      endpoint,
      body,
    }: {
      registrationIds: string[];
      endpoint: (registrationId: string) => string;
      body: Record<string, unknown>;
    }) => {
      const results = await Promise.allSettled(
        registrationIds.map(registrationId => api.post(endpoint(registrationId), body))
      );
      const succeeded = results.filter(result => result.status === 'fulfilled').length;
      const failed = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map(result => result.reason);
      return { total: registrationIds.length, succeeded, failed };
    },
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['groups', id, 'registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['groups'] }),
      ]);

      setSelectedRegistrationIds(new Set());

      if (result.succeeded > 0) {
        toast.success(`${result.succeeded} van ${result.total} registraties verwerkt`);
      }
      if (result.failed.length > 0) {
        const firstError = result.failed[0];
        const message = firstError instanceof Error ? firstError.message : 'Onbekende fout';
        toast.error(`${result.failed.length} mislukt. Eerste fout: ${message}`);
      }
    },
  });

  // Single pass to find the selected rows; both selectedRegistrations and
  // bulkEligible derive from this to avoid iterating registrationRows twice.
  const selectedRows = useMemo(
    () => registrationRows.filter(row => selectedRegistrationIds.has(row.registration.id)),
    [registrationRows, selectedRegistrationIds]
  );
  const selectedRegistrations = useMemo(() => selectedRows.map(row => row.registration), [selectedRows]);
  const bulkEligible = useMemo(
    () => ({
      createMissingDeposits: selectedRows
        .filter(row => row.canRegenerateMissingDepositInvoices)
        .map(row => row.registration),
      createBalanceInvoices: selectedRows.filter(row => row.canCreateBalanceInvoice).map(row => row.registration),
      cancel: selectedRows.filter(row => row.canCancelRegistration).map(row => row.registration),
      reactivate: selectedRows.filter(row => row.canReactivateRegistration).map(row => row.registration),
      delete: selectedRows.filter(row => Boolean(row.registration.cancelledAt)).map(row => row.registration),
    }),
    [selectedRows]
  );

  const bulkDelete = useMutation({
    mutationFn: async (registrationIds: string[]) => {
      const results = await Promise.allSettled(
        registrationIds.map(registrationId => api.delete(`/registrations/${registrationId}`))
      );
      return {
        total: registrationIds.length,
        succeeded: results.filter(result => result.status === 'fulfilled').length,
        failed: results
          .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
          .map(result => result.reason),
      };
    },
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['groups', id, 'registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['registrations'] }),
        queryClient.invalidateQueries({ queryKey: ['groups'] }),
      ]);
      setSelectedRegistrationIds(new Set());
      setIsDeleteDialogOpen(false);
      if (result.succeeded > 0) toast.success(`${result.succeeded} registratie(s) verwijderd`);
      if (result.failed.length > 0) toast.error(`${result.failed.length} registratie(s) konden niet verwijderd worden`);
    },
  });

  const toggleRegistrationSelection = useCallback((registrationId: string, checked: boolean) => {
    setSelectedRegistrationIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(registrationId);
      } else {
        next.delete(registrationId);
      }
      return next;
    });
  }, []);

  const columns = useMemo<ColumnDef<RegistrationOverviewRow>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => {
          const pageRegistrationIds = table.getRowModel().rows.map(row => row.original.registration.id);
          const allSelected =
            pageRegistrationIds.length > 0 &&
            pageRegistrationIds.every(registrationId => selectedRegistrationIds.has(registrationId));
          const someSelected =
            !allSelected && pageRegistrationIds.some(registrationId => selectedRegistrationIds.has(registrationId));

          return (
            <SelectionCheckbox
              checked={allSelected}
              onChange={shouldSelect => {
                setSelectedRegistrationIds(prev => {
                  const next = new Set(prev);
                  for (const registrationId of pageRegistrationIds) {
                    if (shouldSelect) {
                      next.add(registrationId);
                    } else {
                      next.delete(registrationId);
                    }
                  }
                  return next;
                });
              }}
              ariaLabel={
                someSelected
                  ? 'Selecteer alle registraties op deze pagina (gedeeltelijke selectie)'
                  : 'Selecteer alle registraties op deze pagina'
              }
            />
          );
        },
        cell: ({ row }) => {
          const cancelled = Boolean(row.original.registration.cancelledAt);
          return (
            <div className="flex items-center gap-2">
              <SelectionCheckbox
                checked={selectedRegistrationIds.has(row.original.registration.id)}
                onChange={value => toggleRegistrationSelection(row.original.registration.id, value)}
                ariaLabel={`Selecteer ${row.original.registration.email}`}
              />
              {cancelled ? (
                <PlaneLanding className="h-4 w-4 text-muted-foreground" />
              ) : (
                <PlaneTakeoff className="h-4 w-4 text-emerald-600" />
              )}
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'registration.email',
        header: 'Registratie',
        cell: ({ row }) => {
          const cancelled = Boolean(row.original.registration.cancelledAt);
          return (
            <div className="flex items-center gap-2">
              <Link
                to={`/groepen/${id}/registraties/${row.original.registration.id}`}
                className={`font-medium hover:underline ${cancelled ? 'text-muted-foreground' : 'text-primary'}`}
              >
                {row.original.registration.email}
              </Link>
              {cancelled && <Badge variant="secondary">Geannuleerd</Badge>}
              {row.original.hasBalanceInvoice && <Badge variant="warning">Gesloten</Badge>}
            </div>
          );
        },
      },
      {
        id: 'companies',
        header: 'Bedrijf',
        accessorFn: row => row.combinedCompanies,
        cell: ({ row }) => <span>{row.original.combinedCompanies}</span>,
        filterFn: (row, _, filterValue) => {
          return row.original.combinedCompanies.toLowerCase().includes(filterValue.toLowerCase());
        },
      },
      {
        accessorKey: 'registration.lastSubmittedAt',
        header: 'Laatst ingediend',
        cell: ({ row }) => formatDateTime(row.original.registration.lastSubmittedAt),
      },
      {
        id: 'paid',
        header: 'Betaald',
        cell: ({ row }) => {
          const status = row.original.consolidatedPaymentStatus;
          if (status === 'paid') return <Badge variant="success">Betaald</Badge>;
          if (status === 'partial') return <Badge variant="warning">Gedeeltelijk</Badge>;
          return <Badge variant="secondary">Niet betaald</Badge>;
        },
      },
      {
        id: 'depositInvoice',
        header: 'Voorschotfactuur',
        cell: ({ row }) => {
          const status = row.original.depositInvoiceStatus;
          if (status === 'failed') return <Badge variant="destructive">Mislukt</Badge>;
          if (status === 'created') return <Badge variant="success">Aangemaakt</Badge>;
          if (status === 'partial') return <Badge variant="warning">Gedeeltelijk</Badge>;
          if (status === 'none') return null;
          return <Badge variant="secondary">Niet aangemaakt</Badge>;
        },
      },
      {
        id: 'balanceInvoice',
        header: 'Saldo-factuur',
        cell: ({ row }) => {
          const status = row.original.balanceInvoiceStatus;
          if (status === 'failed') return <Badge variant="destructive">Mislukt</Badge>;
          if (status === 'created') return <Badge variant="success">Aangemaakt</Badge>;
          if (status === 'partial') return <Badge variant="warning">Gedeeltelijk</Badge>;
          return <Badge variant="secondary">Niet aangemaakt</Badge>;
        },
      },
      {
        id: 'splitBilling',
        header: 'Split Billing',
        cell: ({ row }) =>
          usesEffectiveSplitBilling(row.original.registration) ? (
            <Badge variant="secondary" className="text-xs">
              Split
            </Badge>
          ) : null,
      },
      // selectedRegistrationIds is read in the select column's header/cell renderers;
      // id is used in the email link. Both must be deps to keep closures fresh.
    ],
    [id, selectedRegistrationIds, toggleRegistrationSelection]
  );

  const table = useReactTable({
    data: registrationRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: (row, _, filterValue) => {
      const query = String(filterValue || '')
        .trim()
        .toLowerCase();
      if (!query) return true;

      const email = row.original.registration.email.toLowerCase();
      const companies = row.original.combinedCompanies.toLowerCase();
      return email.includes(query) || companies.includes(query);
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  if (groupLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Groep niet gevonden</p>
        <Button asChild variant="link">
          <Link to="/groepen">Terug naar groepen</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/groepen">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{getLocalizedText(group.name)}</h1>
          <p className="text-muted-foreground">Groep registraties en details</p>
        </div>
        <input
          ref={tsvInputRef}
          type="file"
          accept=".tsv,text/tab-separated-values"
          className="hidden"
          onChange={handleTsvFileSelected}
        />
        <Button variant="outline" size="sm" onClick={() => tsvInputRef.current?.click()} disabled={tsvImportPending}>
          <Upload className="mr-2 h-4 w-4" />
          {tsvImportPending ? 'Importeren...' : 'Importeer TSV'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => exportSheet.mutate()} disabled={exportSheet.isPending}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          {exportSheet.isPending ? 'Exporteren...' : 'Exporteer naar Sheets'}
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/groepen/${group.id}/bewerken`}>
            <Pencil className="mr-2 h-4 w-4" />
            Bewerken
          </Link>
        </Button>
        <Badge variant={group.isActive ? 'success' : 'secondary'}>{group.isActive ? 'Actief' : 'Inactief'}</Badge>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Groep Informatie</CardTitle>
            <CardDescription>Pakketprijs (pp) en projectgegevens</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Pakketprijs (pp)</p>
                <p className="font-medium text-lg">{formatCurrency(group.packagePrice)}</p>
              </div>
              <div className="p-1">
                <p className="text-sm text-muted-foreground">Groep factuurlijnen</p>
                <p className="font-medium">
                  {groupInvoiceAdjustmentsStats.count} regel{groupInvoiceAdjustmentsStats.count === 1 ? '' : 's'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Totaal {formatCurrency(groupInvoiceAdjustmentsStats.total)}
                </p>
              </div>
            </div>
            {group.project && (
              <div>
                <p className="text-sm text-muted-foreground">Project</p>
                <Link to={`/projecten/${group.project.id}`} className="font-medium text-primary hover:underline">
                  {getLocalizedText(group.project.name)}
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Registraties</CardTitle>
            <CardDescription>Overzicht van alle registraties</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-muted-foreground">Totaal aantal</p>
                <p className="font-medium text-2xl">{registrations.length}</p>
              </div>
              {registrations.some(r => r.isSplitBilling) && (
                <div>
                  <p className="text-sm text-muted-foreground">Split billing</p>
                  <p className="font-medium">{registrations.filter(r => r.isSplitBilling).length}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registraties Overzicht</CardTitle>
          <CardDescription>Klik op een registratie voor meer details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Input
              placeholder="Zoeken op e-mail of bedrijfsnaam..."
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedRegistrations.length === 0
                  ? 'Geen registraties geselecteerd'
                  : `${selectedRegistrations.length} geselecteerd`}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkAction.isPending || bulkEligible.createMissingDeposits.length === 0}
                onClick={() =>
                  bulkAction.mutate({
                    registrationIds: bulkEligible.createMissingDeposits.map(registration => registration.id),
                    endpoint: registrationId => `/registrations/${registrationId}/regenerate-deposit-invoices`,
                    body: {},
                  })
                }
              >
                Ontbrekende voorschot-facturen
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkAction.isPending || bulkEligible.createBalanceInvoices.length === 0}
                onClick={() =>
                  bulkAction.mutate({
                    registrationIds: bulkEligible.createBalanceInvoices.map(registration => registration.id),
                    endpoint: registrationId => `/registrations/${registrationId}/create-balance-invoice`,
                    body: {},
                  })
                }
              >
                Saldo-facturen
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkAction.isPending || bulkEligible.cancel.length === 0}
                onClick={() =>
                  bulkAction.mutate({
                    registrationIds: bulkEligible.cancel.map(registration => registration.id),
                    endpoint: registrationId => `/registrations/${registrationId}/cancel`,
                    body: { reason: 'Bulk annulering vanuit registraties overzicht' },
                  })
                }
              >
                Annuleren
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={bulkAction.isPending || bulkEligible.reactivate.length === 0}
                onClick={() =>
                  bulkAction.mutate({
                    registrationIds: bulkEligible.reactivate.map(registration => registration.id),
                    endpoint: registrationId => `/registrations/${registrationId}/reactivate`,
                    body: {},
                  })
                }
              >
                Heractiveren
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={
                  bulkDelete.isPending ||
                  selectedRows.length === 0 ||
                  bulkEligible.delete.length !== selectedRows.length
                }
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                Verwijderen
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Alleen geannuleerde registraties kunnen verwijderd worden.</p>
          </div>

          {registrationsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nog geen registraties voor deze groep</p>
              <p className="text-sm mt-2">Registraties worden automatisch aangemaakt via webhooks</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map(headerGroup => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map(row => (
                        <TableRow
                          key={row.id}
                          data-state={selectedRegistrationIds.has(row.original.registration.id) && 'selected'}
                          className={`hover:bg-muted/50 ${
                            row.original.registration.cancelledAt ? 'bg-muted/30 text-muted-foreground' : ''
                          }`}
                        >
                          {row.getVisibleCells().map(cell => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                          Geen resultaten gevonden
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {table.getPageCount() > 1 && (
                <div className="flex items-center justify-end space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    Vorige
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    Volgende
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Registraties verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Ben je zeker dat je deze {bulkEligible.delete.length} geannuleerde registratie(s) wilt verwijderen? Ze
              verdwijnen uit het overzicht, maar blijven bewaard in de historiek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={
                bulkDelete.isPending || selectedRows.length === 0 || bulkEligible.delete.length !== selectedRows.length
              }
              onClick={() => bulkDelete.mutate(bulkEligible.delete.map(registration => registration.id))}
            >
              {bulkDelete.isPending ? 'Bezig...' : 'Verwijderen'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
